import React, { useEffect } from 'react';
import { CaptainKork } from './mascot/CaptainKork';
import { getKorkXpLine, getKorkXpMood, getKorkXpMotion, getKorkXpVariant } from '../mascot/captainKork';
import { getKorkXpAudio } from '../mascot/captainKorkAudio';
import { isKorkVoiceEnabled } from '../utils/settings';

export function XpToast({ change, visible }) {
  const hasToast = Boolean(visible && change);
  const awarded = Number(change?.awarded || 0);
  const isPreview = Boolean(change?.preview || change?.eligible === false);
  const isNegative = awarded < 0;
  const deltaText = `${isNegative ? '' : '+'}${awarded} XP`;
  const imageUrl = String(change?.imageUrl || change?.image_url || '').trim();
  const korkLine = getKorkXpLine(change);
  const korkMood = getKorkXpMood(change);
  const korkMotion = getKorkXpMotion(change);
  const korkVariant = getKorkXpVariant(change);
  const korkAudioUrl = getKorkXpAudio(change, korkLine);
  const cardBackground = isPreview
    ? 'linear-gradient(180deg, #eff8ff 0%, #dceffc 100%)'
    : isNegative
    ? 'linear-gradient(180deg, #fff7f2 0%, #ffe0d7 100%)'
    : 'linear-gradient(180deg, #fff8dc 0%, #f1d18d 100%)';
  const accentColor = isPreview ? '#0b83c5' : isNegative ? '#c62828' : '#ff9100';

  useEffect(() => {
    if (!hasToast || !korkAudioUrl || !isKorkVoiceEnabled()) {
      return undefined;
    }

    let audio = null;
    const timer = window.setTimeout(() => {
      try {
        audio = new window.Audio(korkAudioUrl);
        audio.volume = 0.86;
        audio.play().catch(() => {});
      } catch (error) {}
    }, 120);

    return () => {
      window.clearTimeout(timer);
      if (audio) {
        try {
          audio.pause();
        } catch (error) {}
      }
    };
  }, [change?.id, hasToast, korkAudioUrl]);

  if (!hasToast) return null;

  return (
    <div
      className="pfp-xp-toast-overlay"
      aria-live="polite"
      role="status"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 18,
        pointerEvents: 'none',
        boxSizing: 'border-box',
      }}
    >
      <div
          className={`pfp-xp-toast ${isPreview ? 'is-preview' : isNegative ? 'is-negative' : 'is-positive'}`}
        style={{
          position: 'relative',
          inset: 'auto',
          right: 'auto',
          bottom: 'auto',
          width: 'min(92vw, 330px)',
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: '1fr',
          justifyItems: 'center',
          gap: 8,
          padding: '18px 18px 16px',
          borderRadius: 22,
          color: '#17344a',
          textAlign: 'center',
          background: cardBackground,
          border: '2px solid rgba(122, 75, 23, 0.28)',
          boxShadow: '0 24px 70px rgba(42, 28, 8, 0.34), inset 0 0 0 1px rgba(255, 255, 255, 0.5)',
          boxSizing: 'border-box',
          overflow: 'visible',
          transform: 'translateZ(0)',
          animation: 'pfp-xp-toast-card-in 0.24s ease-out, pfp-xp-toast-card-out 0.32s ease-in 1.86s forwards',
        }}
      >
        <div
          className="pfp-xp-toast__mascot"
          style={{
            width: 142,
            height: 144,
            display: 'grid',
            placeItems: 'center',
            overflow: 'visible',
            marginTop: -4,
          }}
        >
          <CaptainKork mood={korkMood} motion={korkMotion} size="large" variant={korkVariant} label="Kaept'n Kork" />
        </div>

        <div
          className="pfp-kork-speech pfp-xp-toast__line"
          style={{
            position: 'relative',
            width: '100%',
            padding: '10px 12px',
            borderRadius: 14,
            background: 'rgba(255, 255, 255, 0.9)',
            color: '#17344a',
            fontSize: '0.92rem',
            fontWeight: 900,
            lineHeight: 1.25,
            boxShadow: 'inset 0 0 0 1px rgba(122, 75, 23, 0.12)',
            boxSizing: 'border-box',
          }}
        >
          {korkLine}
        </div>

        <div
          className="pfp-xp-toast__meta"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 9,
            minWidth: 0,
            width: '100%',
          }}
        >
          <span
            className="pfp-xp-toast__icon"
            style={{
              display: 'inline-grid',
              placeItems: 'center',
              width: 34,
              height: 34,
              flex: '0 0 34px',
              borderRadius: 11,
              background: 'rgba(255, 255, 255, 0.76)',
              boxShadow: 'inset 0 0 0 1px rgba(122, 75, 23, 0.1)',
              color: accentColor,
              fontSize: '0.88rem',
              fontWeight: 1000,
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt=""
                loading="eager"
                style={{
                  width: 26,
                  height: 26,
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 3px 6px rgba(0, 0, 0, 0.2))',
                }}
              />
            ) : (
              'XP'
            )}
          </span>

          <span style={{ minWidth: 0, textAlign: 'left' }}>
            <span
              className="pfp-xp-toast__label"
              style={{
                display: 'block',
                color: '#0c3563',
                fontSize: '0.95rem',
                fontWeight: 950,
                overflowWrap: 'anywhere',
              }}
            >
              {change.label || 'XP geaendert'}
            </span>
            <span
              className="pfp-xp-toast__delta"
              style={{
                display: 'inline-flex',
                marginTop: 2,
                color: accentColor,
                fontSize: '1.52rem',
                fontWeight: 1000,
                letterSpacing: 0,
                lineHeight: 1,
              }}
            >
              {deltaText}
            </span>
          </span>
        </div>

        {isPreview ? (
          <div
            className="pfp-xp-toast__total"
            style={{
              color: '#406176',
              fontSize: '0.84rem',
              fontWeight: 800,
            }}
          >
            {change.account_hint || 'Mit Account sammelst du XP, Level und Achievements.'}
          </div>
        ) : (
          <div
            className="pfp-xp-toast__total"
            style={{
              color: '#6f5632',
              fontSize: '0.84rem',
              fontWeight: 800,
            }}
          >
            Neuer Stand: {Number(change.xp || 0)} XP
          </div>
        )}
      </div>
    </div>
  );
}
